import { initializePaddle } from "@paddle/paddle-js";

// ============================================================================
// Paddle.js — client-side initialization.
//
// Reads the Paddle CLIENT-SIDE token from a frontend-safe env var. The token
// is the public "client-side token" Paddle issues for its in-browser
// checkout SDK; it is intentionally exposed to the browser by Paddle's
// design and is NOT the server-only PADDLE_API_KEY (used for server-to-server
// auth from the backend) or PADDLE_WEBHOOK_SECRET (used to verify webhook
// signatures on the backend). Those two MUST remain server-side only.
//
// Frontend-safe mapping:
//   Platform deployment secret name : PADDLE_CLIENT_TOKEN
//   Frontend (CRA) env var name     : REACT_APP_PADDLE_CLIENT_TOKEN
//
// Create React App will only expose env vars prefixed with REACT_APP_ to the
// browser bundle, so the deployment secret must be mirrored (or set directly)
// as REACT_APP_PADDLE_CLIENT_TOKEN in /app/frontend/.env at build time. The
// underlying secret value is identical — the rename is purely the
// CRA-required public prefix.
//
// Price IDs (also CRA-exposed) follow the same REACT_APP_* mapping:
//   REACT_APP_PADDLE_FIELD_PRO_MONTHLY_PRICE_ID
//   REACT_APP_PADDLE_FIELD_PRO_ANNUAL_PRICE_ID
//   REACT_APP_PADDLE_PROFESSIONAL_MONTHLY_PRICE_ID
//   REACT_APP_PADDLE_PROFESSIONAL_ANNUAL_PRICE_ID
//
// Initialization is lazy + memoized: getPaddle() returns the same Paddle
// instance across calls. Calling getPaddle() when the token is missing
// resolves to null (no throw) so the rest of the app stays functional in
// pre-rollout / dev environments.
// ============================================================================

const PADDLE_ENVIRONMENT = (process.env.REACT_APP_PADDLE_ENVIRONMENT || "production");

// Frontend-safe client-side token (CRA REACT_APP_ prefix is required for the
// value to be present in the browser bundle).
export const PADDLE_CLIENT_TOKEN =
  process.env.REACT_APP_PADDLE_CLIENT_TOKEN || "";

// Frontend-safe price IDs. Empty string when not yet configured.
// Canonical env names (REACT_APP_PADDLE_PRICE_ID_*) take precedence; the
// legacy *_PRICE_ID names remain as fallback so older deployments keep working.
export const PADDLE_PRICE_IDS = {
  // Pro — NEW checkouts use ONLY the v2 live prices ($20/mo, $200/yr, 7-day
  // trial). Legacy $29/$290 price IDs stay live in Paddle for existing
  // subscribers but are no longer sold.
  field_pro_monthly:
    process.env.REACT_APP_PADDLE_PRICE_ID_PRO_MONTHLY_V2 ||
    "pri_01kze02mzxzj166cm871wjb24a",
  field_pro_annual:
    process.env.REACT_APP_PADDLE_PRICE_ID_PRO_ANNUAL_V2 ||
    "pri_01kze0pkregw8d5shxgy56ydng",
  professional_monthly:
    process.env.REACT_APP_PADDLE_PRICE_ID_PROFESSIONAL_MONTHLY ||
    process.env.REACT_APP_PADDLE_PROFESSIONAL_MONTHLY_PRICE_ID || "",
  professional_annual:
    process.env.REACT_APP_PADDLE_PRICE_ID_PROFESSIONAL_ANNUAL ||
    process.env.REACT_APP_PADDLE_PROFESSIONAL_ANNUAL_PRICE_ID || "",
};

/**
 * isPaddleConfigured — minimal readiness signal for the UI to decide
 * whether to enable Paddle-driven checkout buttons. Returns true only when
 * the client-side token is present. Does not validate the token shape and
 * never logs the token value.
 */
export function isPaddleConfigured() {
  return Boolean(PADDLE_CLIENT_TOKEN);
}

/**
 * arePaddlePricesConfigured — true only when all four required price IDs
 * have been provided via env. The pricing page uses this to decide whether
 * "Subscribe via Paddle" CTAs can be safely activated.
 */
export function arePaddlePricesConfigured() {
  return Object.values(PADDLE_PRICE_IDS).every((v) => Boolean(v));
}

let _paddleInstance = null;
let _paddleInitPromise = null;

/**
 * getPaddle — lazily initialize and return the singleton Paddle instance.
 *
 * Resolves to:
 *   - The Paddle instance when initialization succeeds.
 *   - null when the client-side token is not configured (caller should
 *     treat Paddle checkout as unavailable).
 *
 * The token is never logged. Errors during initialization are surfaced via
 * a thrown rejection (the caller chooses how to handle).
 */
export async function getPaddle() {
  if (_paddleInstance) return _paddleInstance;
  if (!isPaddleConfigured()) return null;
  if (!_paddleInitPromise) {
    _paddleInitPromise = initializePaddle({
      environment: PADDLE_ENVIRONMENT,
      token: PADDLE_CLIENT_TOKEN,
      // Re-broadcast Paddle checkout lifecycle events as a window event so
      // pages (e.g. /subscribe) can react to checkout.completed /
      // checkout.closed without owning the singleton init.
      eventCallback: (event) => {
        try {
          window.dispatchEvent(
            new CustomEvent("rsc:paddle-event", { detail: { name: (event && event.name) || "" } })
          );
        } catch (_) { /* ignore */ }
      },
    }).then((paddle) => {
      _paddleInstance = paddle || null;
      return _paddleInstance;
    });
  }
  return _paddleInitPromise;
}

/**
 * openPaddleCheckout — convenience helper to open Paddle's in-browser
 * checkout for a given priceId. Returns false (does not open) if Paddle is
 * not configured or the priceId is missing. The caller is responsible for
 * resolving the correct priceId from PADDLE_PRICE_IDS based on the chosen
 * plan + cadence.
 *
 * options:
 *   - customerEmail   pre-fill the buyer's billing email (may differ from
 *                     the authenticated app email — e.g. team admin pays
 *                     for individual seats).
 *   - successUrl      where Paddle redirects after a completed checkout.
 *   - customData      arbitrary app-defined identity payload that Paddle
 *                     persists on the subscription/transaction and surfaces
 *                     back on every webhook event as `data.custom_data`.
 *                     RigSentinel binds entitlements to the authenticated
 *                     app userId by passing:
 *                         { appUserId, appEmail }
 *                     so the entitlement always lands on the signed-in
 *                     user — independent of the buyer/billing email.
 */
export async function openPaddleCheckout(priceId, options = {}) {
  if (!priceId) return false;
  const paddle = await getPaddle();
  if (!paddle) return false;
  const { customerEmail, successUrl, customData, discountId } = options;
  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    ...(customerEmail ? { customer: { email: customerEmail } } : {}),
    ...(successUrl ? { settings: { successUrl } } : {}),
    ...(customData && typeof customData === "object" ? { customData } : {}),
    ...(discountId ? { discountId } : {}),
  });
  return true;
}
